import { isFiatAuthenticationError, sendFiatTransition } from '@funkit/api-base'

import type { IdentityAssertionProvider } from './identityAssertion'

type TransportArgs = Parameters<typeof sendFiatTransition>[0]

/**
 * Sends a fiat transition, retrying once with a fresh identity assertion.
 *
 * Once, and only on an authentication error. Assertions are short lived, so
 * one expiring mid-request is ordinary and should not reach the user. Past a
 * single retry it is no longer an expiry but a rejected identity, and looping
 * on that hammers the host's mint endpoint while the user waits for a failure
 * that will not change.
 */
export async function sendAuthedFiatTransition<T>(
  args: Omit<TransportArgs, 'getIdentityAssertion'>,
  identityAssertionProvider: IdentityAssertionProvider | undefined,
): Promise<T> {
  if (identityAssertionProvider === undefined) {
    return sendFiatTransition<T>(args)
  }
  const send = () =>
    sendFiatTransition<T>({
      ...args,
      getIdentityAssertion: identityAssertionProvider.get,
    })
  try {
    return await send()
  } catch (error) {
    if (!isFiatAuthenticationError(error)) {
      throw error
    }
    // A host minting short-lived or rejected tokens otherwise shows up only
    // as quietly doubled fiat traffic.
    args.logger.info('connect-core:fiat:identity_assertion_refreshed', {
      endpoint: args.params.endpoint,
    })
    identityAssertionProvider.invalidate()
    return send()
  }
}
